import type { ReactNode } from 'react';

/**
 * Label-over-value definition list for the user detail page.
 *
 * The old identity card was a single key/value column where a mono id, a
 * timestamp, a checkmark and a link each set their own font and height —
 * the uneven rhythm the operator flagged. Here every entry is the same
 * <dt>/<dd> pair in a two-column grid (one column on phones, see
 * styles/user-inspect.css), so labels share one style and values share
 * another; only ids, amounts and timestamps opt into mono via `mono`.
 */
export function InfoList({
  children,
  testId,
  label,
}: {
  readonly children: ReactNode;
  readonly testId?: string;
  readonly label?: string;
}): JSX.Element {
  return (
    <dl className="uinspect-info" data-testid={testId} aria-label={label}>
      {children}
    </dl>
  );
}

export function InfoItem({
  label,
  children,
  wide = false,
  mono = false,
  testId,
}: {
  readonly label: ReactNode;
  readonly children: ReactNode;
  /** Span both columns — for long values such as the actor id. */
  readonly wide?: boolean;
  /** Mono font; reserved for ids, amounts and timestamps. */
  readonly mono?: boolean;
  readonly testId?: string;
}): JSX.Element {
  const itemClass = ['uinspect-info-item', wide ? 'uinspect-info-item--wide' : ''].filter(Boolean).join(' ');
  const valueClass = ['uinspect-info-value', mono ? 'uinspect-mono' : ''].filter(Boolean).join(' ');
  return (
    <div className={itemClass} data-testid={testId}>
      <dt className="uinspect-info-label">{label}</dt>
      <dd className={valueClass}>{children}</dd>
    </div>
  );
}
